import React from 'react';
import {
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  StatusBar,
  ImageBackground,
  Platform,
} from 'react-native';
import { useRouter } from 'expo-router';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import Svg, { Path } from 'react-native-svg';
import CustomSlider from '@/components/CustomSlider';
import { useAudioPlayer } from '@/contexts/AudioPlayerContext';

export default function AudioPlayerModal() {
  const router = useRouter();
  const insets = useSafeAreaInsets();
  const { 
    currentTrack, 
    isPlaying, 
    position, 
    duration,
    pause,
    resume,
    stop,
    seekTo,
  } = useAudioPlayer();

  const formatTime = (milliseconds: number) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleClose = () => {
    router.dismiss();
  };

  const handleExit = () => {
    stop();
    router.dismiss();
  };

  const handlePlayPause = () => {
    if (isPlaying) {
      pause();
    } else {
      resume();
    }
  };

  const handleSeek = (value: number) => {
    seekTo(value);
  };

  if (!currentTrack) {
    router.dismiss();
    return null;
  }

  const stepNumberFormatted = currentTrack.stepNumber.toString().padStart(2, '0');

  return (
    <View style={styles.container}>
      <ImageBackground
        source={require('@/assets/images/image_1762871512032.png')}
        style={styles.backgroundImage}
        resizeMode="cover"
      >
        <StatusBar barStyle="dark-content" backgroundColor="transparent" translucent />

        {/* Header - Top safe area only for Android, not iOS */}
        <View style={[styles.header, { paddingTop: Platform.OS === 'android' ? insets.top + 16 : 16 }]}>
          <TouchableOpacity 
            style={styles.backButton}
            onPress={handleClose}
            activeOpacity={0.7}
          >
            <Svg width="24" height="24" viewBox="0 0 24 24" fill="none">
              <Path 
                d="M19.25 8.5L12.25 15.5L5.25 8.5" 
                stroke="#151515" 
                strokeWidth="2" 
                strokeLinecap="square" 
              />
            </Svg>
          </TouchableOpacity>

          <TouchableOpacity onPress={handleExit} activeOpacity={0.7}>
            <Text style={styles.exitButton}>Sair</Text>
          </TouchableOpacity>
        </View>

        {/* Content Area */}
        <View style={styles.content}>
          {/* Step Information */}
          <View style={styles.stepInfo}>
            <Text style={styles.stepTitle}>{currentTrack.stepTitle}</Text>
            <Text style={styles.stepNumber}>{stepNumberFormatted}º Passo</Text>
          </View>

          {/* Play/Pause Button */}
          <TouchableOpacity 
            style={styles.playButtonContainer}
            onPress={handlePlayPause}
            activeOpacity={0.8}
          >
            <View style={styles.playButtonOuter}>
              <View style={styles.playButtonInner}>
                {isPlaying ? (
                  <Svg width="47" height="47" viewBox="0 0 47 47" fill="none">
                    <Path 
                      d="M11.6055 12.5708C11.6055 11.1681 12.7426 10.031 14.1453 10.031H15.8011C17.2038 10.031 18.3409 11.1681 18.3409 12.5708V34.1054C18.3409 35.5081 17.2038 36.6452 15.8011 36.6452H14.1453C12.7426 36.6452 11.6055 35.5081 11.6055 34.1054V12.5708Z" 
                      fill="white" 
                    />
                    <Path 
                      d="M27.0703 12.5708C27.0703 11.1681 28.2074 10.031 29.6101 10.031H31.2659C32.6686 10.031 33.8057 11.1681 33.8057 12.5708V34.1054C33.8057 35.5081 32.6686 36.6452 31.2659 36.6452H29.6101C28.2074 36.6452 27.0703 35.5081 27.0703 34.1054V12.5708Z" 
                      fill="white" 
                    />
                  </Svg>
                ) : (
                  <Svg width="47" height="47" viewBox="0 0 47 47" fill="none">
                    <Path 
                      fillRule="evenodd" 
                      clipRule="evenodd" 
                      d="M11.6047 12.6895L11.6047 33.7273C11.6047 36.0413 13.9829 37.4937 15.8939 36.344L33.3692 25.8332C35.2927 24.6762 35.2941 21.7596 33.3706 20.6012L15.8953 10.0728C13.9843 8.92311 11.6047 10.3741 11.6047 12.6895Z" 
                      fill="white" 
                    />
                  </Svg>
                )}
              </View>
            </View>
          </TouchableOpacity>

          {/* Progress Bar */}
          <View style={styles.progressContainer}>
            <View style={styles.sliderContainer}>
              <CustomSlider
                style={styles.slider}
                minimumValue={0}
                maximumValue={duration}
                value={position}
                onSlidingComplete={handleSeek}
                minimumTrackTintColor="#151515"
                maximumTrackTintColor="rgba(0, 0, 0, 0.1)"
                thumbTintColor="#151515"
              />
            </View>
            <View style={styles.timeContainer}>
              <Text style={styles.timeText}>{formatTime(position)}</Text>
              <Text style={styles.timeText}>{formatTime(duration)}</Text>
            </View>
          </View>
        </View>
      </ImageBackground>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#F9F8F3',
  },
  backgroundImage: {
    flex: 1,
    width: '100%',
    height: '100%',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingBottom: 16,
  },
  backButton: {
    padding: 8,
  },
  exitButton: {
    color: '#015480',
    fontSize: 14,
    fontFamily: 'Inter_400Regular',
    lineHeight: 14,
  },
  content: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'space-around',
    paddingHorizontal: 24,
    position: 'relative',
  },
  stepInfo: {
    alignItems: 'center',
    gap: 4,
    marginTop: 80,
  },
  stepTitle: {
    fontFamily: 'Sentient-Regular',
    fontSize: 32,
    color: '#151515',
    lineHeight: 44.8,
    textAlign: 'center',
  },
  stepNumber: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#7E7D75',
    lineHeight: 19.6,
    textAlign: 'center',
  },
  playButtonContainer: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  playButtonOuter: {
    backgroundColor: 'rgba(61, 60, 56, 0.05)',
    borderRadius: 70,
    width: 140,
    height: 140,
    alignItems: 'center',
    justifyContent: 'center',
  },
  playButtonInner: {
    backgroundColor: '#151515',
    borderRadius: 50,
    width: 100,
    height: 100,
    alignItems: 'center',
    justifyContent: 'center',
  },
  progressContainer: {
    width: '100%',
    maxWidth: 345,
    gap: 16,
    marginBottom: 40,
  },
  sliderContainer: {
    width: '100%',
    height: 20,
    justifyContent: 'center',
  },
  slider: {
    width: '100%',
    height: 20,
  },
  timeContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    width: '100%',
  },
  timeText: {
    fontFamily: 'Inter_400Regular',
    fontSize: 14,
    color: '#151515',
    lineHeight: 19.6,
  },
});
